import React from 'react';
import type { BoxProps, CardProps } from '@mui/material';
import { Box, Card, CardContent, Typography } from '@mui/material';
import { Modal } from '../../../../../../components/modals/v2/helpers/v2.modal.helpers';
import Cart from './Cart';
import UpdateCommission from './UpdateCommission';
import Showable from '../../../../../../components/Showable';
import type { QuoteViewState } from '../new-business-reducer/types';
import type { QuoteViewHandlers } from '../hooks/useNewBusinessQuoteReducer';
import { QuoteCartModalConfig } from '../../../../../../components/modals/v2/QuoteCartModal';
import RenewalToggle from '../../customizeQuote/components/RenewalToggle';

interface BaseHeaderProps extends QuoteViewState {
  children?: React.ReactNode;
  setCommission: QuoteViewHandlers['setCommission'];
  setIsRenewal: QuoteViewHandlers['setIsRenewal'];
}

const NewBusinessRequestQuoteHeader = ({
  children,
  setCommission,
  setIsRenewal,
  ...quoteViewState
}: BaseHeaderProps) => {
  return (
    <>
      <Box
        display="flex"
        flexDirection="column"
        className="contrast-text"
        height="100%"
      >
        <Typography variant="h6" style={{ lineHeight: 1.1 }} color="primary">
          Quote Options for {quoteViewState.accountData.name}
        </Typography>
        <Box
          display="flex"
          gap="0.75rem"
          fontWeight={600}
          style={{ fontSize: '1.1rem' }}
        >
          <div>
            Industry Code: {quoteViewState.accountData.industryCode || ''}
          </div>{' '}
          |
          <div>
            Description:{' '}
            {quoteViewState.accountData.ui_secondaryIndustryDescription || ''}
          </div>
        </Box>

        <RenewalToggle
          handleToggle={setIsRenewal}
          toggleOn={!!quoteViewState.isRenewal}
          accountData={quoteViewState.accountData}
          productType={quoteViewState.productType}
        />
      </Box>
      <Box>
        <Box display="flex" gap="2rem" alignItems="center" height="100%">
          {children}
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            height="100%"
            justifyContent="center"
            mt={2}
          >
            <Cart
              onClick={Modal.deferShow(QuoteCartModalConfig, {
                data: {
                  accountData: quoteViewState.accountData,
                  accountId: quoteViewState.accountData.id,
                  productType: quoteViewState.productType,
                  uiSettings: quoteViewState.uiSettings,
                  isRenewal: quoteViewState.isRenewal,
                },
              })}
              quoteCount={quoteViewState.cartList?.count || 0}
            />
            Cart
          </Box>
        </Box>
        <Showable show={!!quoteViewState.uiSettings.renderCommissionSlider}>
          <UpdateCommission
            commissionInput={quoteViewState.commission}
            setCommissionInput={setCommission}
            accountId={quoteViewState.accountId}
          />
        </Showable>
      </Box>
    </>
  );
};

export default NewBusinessRequestQuoteHeader;

export const CardWrapper = ({
  children,
  style = {},
  boxProps = {},
  cardStyles = {},
  cardProps = {},
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
  cardStyles?: React.CSSProperties;
  boxProps?: BoxProps;
  cardProps?: CardProps;
}) => {
  return (
    <Card
      elevation={4}
      style={{
        marginBottom: '1rem',
        overflow: 'visible',
        paddingBottom: 0,
        ...style,
      }}
      {...cardProps}
      className={RATER_X_CARD_WRAPPER_CLASS_NAME}
    >
      <CardContent
        style={{ paddingBottom: '1rem', paddingTop: '1rem', ...cardStyles }}
        className="contrast-text"
      >
        <Box
          py={2}
          px={2}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          {...boxProps}
        >
          {children}
        </Box>
      </CardContent>
    </Card>
  );
};

export const RATER_X_CARD_WRAPPER_CLASS_NAME = 'rater-x-card-wrapper';
